import { en as enLocalizations } from "common/config/localizations";
import type { VenueDetailsBySlugResponse } from "common/hooks/business/details/useVenueDetailsBySlug";
import type { Venue } from "common/types";
import { sanitiseMetaDescription } from "@/lib/meta";
import { slugToTitle } from "@/lib/slug";

export type WhatsNearbyListVenue = { name: string; href: string };

export type WhatsNearbyLoaderData = {
  venue: VenueDetailsBySlugResponse | null;
  /** The list's venues as crawlable links; empty unless the anonymous fetch succeeded. */
  listVenues: WhatsNearbyListVenue[];
  canonicalOrigin: string;
};

/**
 * The canonical venue page for a nearby venue — the same URL the sitemap lists.
 *
 * @param venue A nearby venue.
 * @returns `/venuedetails/<slug>`, or undefined when the venue has no slug.
 */
export function venuePageHref(venue: Pick<Venue, "business">): string | undefined {
  const path = venue.business?.publicUrl || venue.business?.slug;
  return path ? `/venuedetails/${path}` : undefined;
}

type RedirectInput = Pick<
  VenueDetailsBySlugResponse,
  | "details"
  | "canViewPage"
  | "accesibilityCategories"
  | "accesibilityCategoriesHybrid"
  | "accesibilityCategoriesEnterprise"
>;

/**
 * Whether a /venuedetails/ URL is a curated list that lives at /whats-nearby/. A list with its
 * own accessibility guide keeps its venue page, and a denied or gated one keeps the gate.
 *
 * @param venue The venue route loader's payload.
 * @returns True when that loader should 301 to /whats-nearby/.
 */
export function shouldRedirectToWhatsNearby(venue: RedirectInput | null): boolean {
  const details = venue?.details;
  if (!venue || details?.isAreaMap !== true || venue.canViewPage === 0) {
    return false;
  }

  const hasOwnAccessibilityData =
    (venue.accesibilityCategories?.length ?? 0) > 0 ||
    (venue.accesibilityCategoriesHybrid?.length ?? 0) > 0 ||
    (venue.accesibilityCategoriesEnterprise?.length ?? 0) > 0;

  return (
    !hasOwnAccessibilityData &&
    Number(details.business?.isPasswordProtect) !== 1 &&
    Number(details.business?.isSsoProtect) !== 1
  );
}

/**
 * Robots value for a what's-nearby page: only a public, unprotected curated list is indexable.
 *
 * @param data Route loader data.
 * @returns The robots meta content.
 */
export function whatsNearbyRobots(data: WhatsNearbyLoaderData | undefined): string {
  const details = data?.venue?.details;
  if (!details?.isAreaMap) {
    return "noindex, follow";
  }

  // details.isStopSearchEngine follows the visited slug, so it covers a custom-view link too.
  const business = details.business;
  const isProtected =
    data?.venue?.canViewPage === 0 ||
    Number(details.isStopSearchEngine) === 1 ||
    Number(business?.isPasswordProtect) === 1 ||
    Number(business?.isSsoProtect) === 1 ||
    Number(business?.isStopSearchEngine) === 1;
  if (isProtected) {
    return "noindex, nofollow";
  }

  if (details.mapVisibility === "sociability") {
    return "noindex";
  }

  return details.mapVisibility === "public" ? "index, follow" : "noindex, nofollow";
}

/**
 * Head tags for a what's-nearby page: title, description, canonical, robots and, for an
 * indexable list, ItemList JSON-LD.
 *
 * @param data Route loader data.
 * @param slug The page slug.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta, links and scripts.
 */
export function buildWhatsNearbyHead(
  data: WhatsNearbyLoaderData | undefined,
  slug: string,
  isProduction: boolean,
) {
  const details = data?.venue?.details;
  const origin = data?.canonicalOrigin ?? "";
  const name = details?.name ?? slugToTitle(slug);
  const summary = details?.business?.venue_summary?.trim();
  const description = sanitiseMetaDescription(
    summary
      ? `${name}. ${summary}`
      : enLocalizations.whatsNearbyListDescription.replace("%{name}", name),
  );
  const robots = whatsNearbyRobots(data);
  const listVenues = data?.listVenues ?? [];

  return {
    meta: [
      { title: `${name} | ${enLocalizations.whatsNearbyTitleSuffix}` },
      { name: "description", content: description },
      ...(isProduction ? [{ name: "robots", content: robots }] : []),
    ],
    links: [{ rel: "canonical", href: `${origin}/whats-nearby/${slug}` }],
    scripts:
      robots === "index, follow" && listVenues.length > 0
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ItemList",
                name,
                itemListElement: listVenues.map((venue, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  url: `${origin}${venue.href}`,
                  name: venue.name,
                })),
              }),
            },
          ]
        : [],
  };
}
